import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { setCanonical, setMeta } from '@/utils/seo';
import { BreadcrumbSchema, SpeakableSchema, FAQSchema, ServiceSchema } from '@/components/SEOSchemas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SPACountryPage() {
  const { slug } = useParams();
  const [country, setCountry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/countries/${slug}`).then(r => {
      setCountry(r.data);
      setCanonical(`/spa/${slug}`);
      setMeta(`Special Power of Attorney from ${r.data.name} | LegaSecure Guide`, `How to create an SPA (Special Power of Attorney) from ${r.data.name}. Online, Apostille-ready SPA for OFWs in ${r.data.name}.`);
    }).catch(() => setCountry(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="ls-container py-20 text-center"><i className="fa-solid fa-spinner fa-spin text-2xl text-[var(--ls-navy)]"></i></div>;
  if (!country) return <div className="ls-container py-20 text-center"><h1 className="text-2xl font-bold">Country not found</h1></div>;

  const faqs = [
    { question: `How do I get an SPA from ${country.name}?`, answer: `Traditionally, you'd visit the Philippine Consulate in ${country.name} for notarization. With LegaSecure, you can create an Apostille-ready Online SPA digitally from anywhere in ${country.name}.` },
    { question: `How much does an SPA cost from ${country.name}?`, answer: `Traditional SPA through a consulate can cost $50-200+ plus courier fees. LegaSecure's Standard plan at P249/month includes unlimited Online SPA generation.` },
    { question: `Can my family use the SPA to sell my property?`, answer: `Yes, an SPA specifically granting authority to sell property allows your designated agent to execute the sale on your behalf. Make sure the SPA clearly states the property details and the authority granted.` },
  ];

  return (
    <div data-testid="spa-country-page">
      <BreadcrumbSchema items={[{ name: 'Home', url: '/' }, { name: 'OFW Countries', url: '/all-countries' }, { name: country.name, url: `/ofw/${slug}` }, { name: 'SPA' }]} />
      <SpeakableSchema selectors={["h1", ".speakable-intro"]} />
      <ServiceSchema />
      <FAQSchema faqs={faqs} />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1920&q=80" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-white/80"></div>
        </div>
        <div className="ls-container relative z-10 py-12">
          <nav className="text-sm text-[var(--ls-text-light)] mb-4">
            <Link to="/" className="hover:text-[var(--ls-gold)]">Home</Link> / <Link to="/all-countries" className="hover:text-[var(--ls-gold)]">OFW Countries</Link> / <Link to={`/ofw/${slug}`} className="hover:text-[var(--ls-gold)]">{country.name}</Link> / <span className="text-[var(--ls-navy)] font-medium">SPA</span>
          </nav>
          <h1 className="text-3xl md:text-4xl font-bold font-['Crimson_Pro'] mb-3" style={{ color: '#1B365D' }}>Special Power of Attorney from {country.name}</h1>
          <p className="speakable-intro text-lg max-w-2xl" style={{ color: '#4A5568' }}>
            Don't waste time and money at a consulate. Create an Apostille-ready SPA from {country.name} in minutes — authorize your family to handle property, banking, and legal matters back home.
          </p>
          <a href="https://legasecure.com" className="ls-btn-navy mt-6 inline-block">Create Your Online SPA</a>
        </div>
      </section>

      <section className="ls-section">
        <div className="ls-container max-w-4xl">
          <div className="ls-card prose-content mb-8">
            <h2>What is a Special Power of Attorney (SPA)?</h2>
            <p>A Special Power of Attorney is a legal document that authorizes someone in the Philippines to act on your behalf for specific tasks. As an OFW in {country.name}, this is essential for managing your Philippine affairs without flying home.</p>
            <h2>Traditional SPA Process from {country.name}</h2>
            <ol>
              <li>Visit the Philippine Consulate or Embassy in {country.name}</li>
              <li>Bring valid IDs and necessary documents</li>
              <li>Pay notarization fees ($50-200+)</li>
              <li>Wait for processing</li>
              <li>Courier the original SPA to the Philippines</li>
              <li>Your agent registers it at the appropriate office</li>
            </ol>
            <p><strong>Total time:</strong> 2-4 weeks. <strong>Total cost:</strong> $100-400+</p>
            <h2>Online SPA with LegaSecure</h2>
            <p>LegaSecure streamlines this process. From your phone or computer in {country.name}, you can create an Apostille-ready Online SPA in minutes. No consulate visits needed.</p>
            <h2>What Can Your Agent Do with the SPA?</h2>
            <ul>
              <li>Sell or buy real property in your name</li>
              <li>Manage bank accounts and financial transactions</li>
              <li>Sign contracts and legal documents</li>
              <li>Process government documents (SSS, Pag-IBIG, PhilHealth)</li>
              <li>Handle tax matters and business operations</li>
            </ul>
          </div>

          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {faqs.map((f, i) => <FAQItem key={i} question={f.question} answer={f.answer} />)}
            </div>
          </div>

          <div className="flex gap-4 flex-wrap">
            <Link to={`/ofw/${slug}`} className="ls-btn-outline text-sm">Back to {country.name} Guide</Link>
            <Link to="/guides/special-power-of-attorney-philippines" className="ls-btn-outline text-sm">Full SPA Guide</Link>
          </div>
        </div>
      </section>

      <section className="bg-[var(--ls-bg)] py-12">
        <div className="ls-container text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Ready to Create Your SPA from {country.name}?</h2>
          <a href="https://legasecure.com" className="ls-btn-gold text-lg px-8 py-3">Get Started <i className="fa-solid fa-arrow-right ml-2"></i></a>
        </div>
      </section>
    </div>
  );
}

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="ls-card cursor-pointer" onClick={() => setOpen(!open)}>
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-sm text-[var(--ls-navy)] pr-4">{question}</h3>
        <i className={`fa-solid fa-chevron-down text-[var(--ls-text-light)] transition-transform text-xs ${open ? 'rotate-180' : ''}`}></i>
      </div>
      {open && <p className="mt-2 text-sm text-[var(--ls-text-light)] leading-relaxed">{answer}</p>}
    </div>
  );
}
